import { formatClock, type HealthAction, type HealthSignal, type HealthSubject, type QueryView } from '@krp/client-runtime'

import {
  AQSI_LINK_ADVICE,
  CABINET_RELOAD_ACTION,
  REGISTER_REFRESH_ACTION,
  REGISTER_REFRESH_LABEL,
  type AqsiLinkAdviceKey,
} from './healthTexts'
import type { AqsiRegisterHealth, AqsiRegisterLink } from './types'

/**
 * Связь кассы AQSI сигналами точки «Связь» (RP-UI-LINK, шаг 4): чистые функции от ответа `pos/registers/{id}/link` и чтения списка касс.
 * Два субъекта — «Кабинет AQSI» (наша служба получила ответ кабинета; уровень словаря SRC-HEALTH) и «Касса AQSI» (аппарат ответил на
 * проверку командой). Причина службы — дословно (`problem`), совет — из одного словаря по коду (`healthTexts.ts`). Экран РМК о связи
 * больше ничего не пишет: «только при сбое» решает точка.
 */

/** Ключ субъекта кабинета: один на окно РМК — и для чтения списка касс, и для связи моей кассы. */
export const CABINET_SUBJECT_ID = 'aqsi.cabinet'

export const registerSubjectId = (deviceId: number) => `aqsi.register.${deviceId}`

const CABINET_SUBJECT: HealthSubject = { kind: 'source', id: CABINET_SUBJECT_ID, title: 'Кабинет AQSI' }

const REFRESH: readonly HealthAction[] = [{ key: REGISTER_REFRESH_ACTION, label: REGISTER_REFRESH_LABEL }]
const RELOAD: readonly HealthAction[] = [{ key: CABINET_RELOAD_ACTION, label: 'Обновить' }]
const NO_ACTIONS: readonly HealthAction[] = []

/** Касса, о которой сигнал: номер у службы и название из кабинета (`null` — не прочитано). */
export interface RegisterRef {
  deviceId: number
  name: string | null
}

const adviceOf = (key: AqsiLinkAdviceKey) => AQSI_LINK_ADVICE[key]

/** «Не отвечает с 12:40» — момент, если он есть. */
const sinceText = (text: string, iso: string | null) => {
  const at = formatClock(iso)
  return at === null ? text : `${text} с ${at}`
}

function registerSubject(register: RegisterRef): HealthSubject {
  return {
    kind: 'register',
    id: registerSubjectId(register.deviceId),
    title: register.name !== null && register.name !== '' ? `Касса AQSI · ${register.name}` : `Касса AQSI №${register.deviceId}`,
  }
}

/** Текст для «Скопировать» администратору: коды службы и моменты как есть. */
function diagnosticsOf(link: AqsiRegisterLink): string {
  return [
    `Касса AQSI №${link.deviceId}`,
    `Кабинет: ${link.server}${link.serverCheckedAt !== null ? ` (${link.serverCheckedAt})` : ''}`,
    ...(link.serverReason !== null ? [`Ответ кабинета: ${link.serverReason}`] : []),
    `Аппарат: ${link.device}${link.reasonCode !== null ? `; код: ${link.reasonCode}` : ''}`,
    ...(link.reason !== null ? [`Причина: ${link.reason}`] : []),
    ...(link.checkedAt !== null ? [`Проверен: ${link.checkedAt}`] : []),
    ...(link.lastActivityAt !== null ? [`Последний отклик в работе: ${link.lastActivityAt}${link.lastActivitySource !== null ? ` (${link.lastActivitySource})` : ''}`] : []),
    `Проверка командой: ${link.probeEnabled ? 'включена' : 'выключена'}${link.cached ? '; показан недавний итог' : ''}`,
    ...(link.nextProbeAt !== null ? [`Следующая проверка не раньше: ${link.nextProbeAt}`] : []),
  ].join('\n')
}

function cabinetOf(link: AqsiRegisterLink, diagnostics: string): HealthSignal {
  switch (link.server) {
    case 'ok':
      return {
        subject: CABINET_SUBJECT,
        level: 'ok',
        summary: 'Отвечает',
        problem: null,
        lastOkAt: link.serverCheckedAt,
        since: null,
        advice: null,
        actions: NO_ACTIONS,
        diagnostics: null,
      }
    case 'error':
      return {
        subject: CABINET_SUBJECT,
        level: 'down',
        summary: sinceText('Не отвечает', link.serverCheckedAt),
        problem: link.serverReason,
        lastOkAt: null,
        since: link.serverCheckedAt,
        advice: adviceOf('cabinet-error'),
        actions: REFRESH,
        diagnostics,
      }
    default:
      return {
        subject: CABINET_SUBJECT,
        level: 'unknown',
        summary: 'Ещё не проверялся',
        problem: link.serverReason,
        lastOkAt: null,
        since: null,
        advice: null,
        actions: NO_ACTIONS,
        diagnostics: null,
      }
  }
}

function registerOf(link: AqsiRegisterLink, subject: HealthSubject, diagnostics: string): HealthSignal {
  const base = { subject, lastOkAt: link.lastActivityAt, actions: REFRESH, diagnostics }
  switch (link.device) {
    case 'online':
      return {
        ...base,
        level: 'ok',
        summary: 'Отвечает',
        problem: null,
        lastOkAt: link.checkedAt ?? link.lastActivityAt,
        since: null,
        advice: null,
        diagnostics: null,
      }
    case 'offline':
      return {
        ...base,
        level: 'down',
        summary: sinceText('Не отвечает', link.checkedAt),
        problem: link.reason,
        since: link.checkedAt,
        advice: adviceOf('no-answer'),
      }
    case 'not-checked':
      // Проверка выключена настройкой или сейчас невозможна (за кассой другой человек): это не отказ, а «не знаем».
      return {
        ...base,
        level: 'unknown',
        summary: link.reasonCode === 'busy' ? 'Сейчас не проверить' : 'Не проверяется',
        problem: link.reason,
        since: null,
        advice: link.reasonCode === 'busy' ? adviceOf('busy') : null,
      }
    default:
      return unknownDevice(link, base)
  }
}

/** Аппарат `unknown`: не проверялся, проверка устарела, кабинет не ответил или не назвал итог. */
function unknownDevice(
  link: AqsiRegisterLink,
  base: Pick<HealthSignal, 'subject' | 'lastOkAt' | 'actions' | 'diagnostics'>,
): HealthSignal {
  switch (link.reasonCode) {
    case 'not-probed':
      return link.checkedAt === null
        ? { ...base, level: 'unknown', summary: 'Ещё не проверялась', problem: null, since: null, advice: adviceOf('not-probed') }
        : {
            ...base,
            level: 'degraded',
            summary: `Давно не проверялась · последняя проверка в ${formatClock(link.checkedAt) ?? '—'}`,
            problem: null,
            since: link.checkedAt,
            advice: adviceOf('stale'),
          }
    case 'cabinet-unavailable':
      return { ...base, level: 'unknown', summary: 'Не проверить: не отвечает кабинет AQSI', problem: link.reason, since: null, advice: adviceOf('cabinet-unavailable') }
    case 'refused':
      return { ...base, level: 'degraded', summary: 'Кабинет AQSI отказал в проверке', problem: link.reason, since: link.checkedAt, advice: adviceOf('refused') }
    default:
      return { ...base, level: 'degraded', summary: 'Ответ не подтверждён', problem: link.reason, since: link.checkedAt, advice: adviceOf('unconfirmed') }
  }
}

/** Пока связь кассы не прочитана: оба субъекта «ещё не проверялись», кассе ничего не отправляется. */
function unread(subject: HealthSubject): AqsiRegisterHealth {
  const empty = { problem: null, lastOkAt: null, since: null, advice: null, diagnostics: null }
  return {
    cabinet: { ...empty, subject: CABINET_SUBJECT, level: 'unknown', summary: 'Ещё не проверялся', actions: NO_ACTIONS },
    register: { ...empty, subject, level: 'unknown', summary: 'Ещё не проверялась', actions: REFRESH },
  }
}

/** Связь кассы AQSI двумя сигналами: кабинет и касса; `undefined` — ответ ещё не пришёл. */
export function aqsiRegisterHealth(link: AqsiRegisterLink | undefined, register: RegisterRef): AqsiRegisterHealth {
  const subject = registerSubject(register)
  if (link === undefined) return unread(subject)
  const diagnostics = diagnosticsOf(link)
  return { cabinet: cabinetOf(link, diagnostics), register: registerOf(link, subject, diagnostics) }
}

/**
 * Кабинет по чтению списка касс — пока работы за кассой нет («Начало работы»): прочитан — отвечает; отказ — дословно текст службы,
 * действие «Обновить». Идёт первое чтение — «ещё не проверялся» (экран сам показывает, что читает).
 */
export function cabinetReadHealth(view: QueryView, readAt: string | null): HealthSignal {
  if (view.status === 'ready' || view.status === 'empty') {
    return { subject: CABINET_SUBJECT, level: 'ok', summary: 'Отвечает', problem: null, lastOkAt: readAt, since: null, advice: null, actions: NO_ACTIONS, diagnostics: null }
  }
  if (view.status === 'loading' || view.status === 'idle') {
    return { subject: CABINET_SUBJECT, level: 'unknown', summary: 'Читаем кассы и смены', problem: null, lastOkAt: readAt, since: null, advice: null, actions: NO_ACTIONS, diagnostics: null }
  }
  const error = view.error
  return {
    subject: CABINET_SUBJECT,
    level: 'down',
    summary: 'Не ответил',
    problem: error?.message ?? null,
    lastOkAt: readAt,
    since: null,
    advice: adviceOf('cabinet-read-failed'),
    actions: RELOAD,
    diagnostics:
      error === null
        ? null
        : ['Кабинет AQSI: список касс не прочитан', `Ошибка: ${error.message}`, `Код: ${error.code}`, ...(error.correlationId !== null ? [`Номер запроса: ${error.correlationId}`] : [])].join('\n'),
  }
}

/** Слова проверки «Актуализировать состояние кассы» для строки итога: оба уровня, как их видит мини-окно. */
export function healthCheckText(health: AqsiRegisterHealth): string {
  const line = (signal: HealthSignal) => `${signal.subject.title}: ${signal.summary}${signal.problem !== null ? ` — ${signal.problem}` : ''}`
  return `${line(health.cabinet)}. ${line(health.register)}`
}
